import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { ResourceFragment } from '@/generated/contentful'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish'
import { capitalize } from '@/utils/string/capitalize'
import { useUrl } from '@/utils/url/useUrl'

import { useResource } from '../useResource'

type Props = {
	children: React.ReactNode
	entryItem: ResourceFragment
}

export const getResourcePageType = (type: string | null | undefined) => {
	if (!type) return 'reports'
	if (['Webinar', 'Webinar TY'].includes(type)) return 'webinars'
	if (['Guide', 'Guide TY'].includes(type)) return 'guides'
	return 'reports'
}

export function ResourceMeta({ children, entryItem }: Props) {
	const { locale = 'en-US' } = useRouter()
	const entry = useResource()
	const url = useUrl({ locale })

	let resource = entryItem

	if ('error' in entry && !entryItem) return null
	if ('data' in entry) resource = entry.data

	const { seoMetadata } = resource ?? {}
	const baseUrl = `${url?.protocol}//${url?.host}`
	const includeTitleTemplate =
		Boolean(seoMetadata?.includeTitleTemplate) && !seoMetadata?.title?.endsWith(' BigCommerce')

	const title = includeTitleTemplate
		? `${seoMetadata?.title} | BigCommerce`
		: seoMetadata?.title ?? `${resource.title} | BigCommerce`

	const description =
		seoMetadata?.description ||
		'Learn how BigCommerce can fuel your business with all the capabilities of enterprise ecommerce—without the cost or complexity.'

	const slug = resource.slug || resource.slug_en_US

	if (!slug) {
		return null
	}

	return (
		<>
			<Head>
				<meta name="og:locale:alternate" content="en_US" key="en_US" />
				<meta name="og:locale:alternate" content="en_GB" key="en_GB" />
				<meta name="og:locale:alternate" content="en_AU" key="en_AU" />
				<meta name="og:locale:alternate" content="fr_FR" key="fr_FR" />
				<meta name="og:locale:alternate" content="it_IT" key="it_IT" />
				<meta name="og:locale:alternate" content="nl_NL" key="nl_NL" />
				<meta name="og:locale:alternate" content="es_ES" key="es_ES" />
				<meta name="og:locale:alternate" content="es_MX" key="es_MX" />
				<meta name="og:locale:alternate" content="de_DE" key="de_DE" />
			</Head>
			<NextSeo
				title={title}
				description={description}
				canonical={
					seoMetadata?.canonicalUrl ||
					`${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/resources/${getResourcePageType(resource.type)}/${slug}/`
					)}`
				}
				languageAlternates={[
					{
						hrefLang: 'en-us',
						href: `${getBaseUrl('en-US')}${getLocalizedFromEnglish(
							'en-US',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_en_US}/`
						)}`
					},
					{
						hrefLang: 'en-au',
						href: `${getBaseUrl('en-AU')}${getLocalizedFromEnglish(
							'en-AU',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_en_AU}/`
						)}`
					},
					{
						hrefLang: 'en-gb',
						href: `${getBaseUrl('en-GB')}${getLocalizedFromEnglish(
							'en-GB',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_en_GB}/`
						)}`
					},
					{
						hrefLang: 'fr-fr',
						href: `${getBaseUrl('fr-FR')}${getLocalizedFromEnglish(
							'fr-FR',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_fr_FR}/`
						)}`
					},
					{
						hrefLang: 'it-it',
						href: `${getBaseUrl('it-IT')}${getLocalizedFromEnglish(
							'it-IT',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_it_IT}/`
						)}`
					},
					{
						hrefLang: 'de-de',
						href: `${getBaseUrl('de-DE')}${getLocalizedFromEnglish(
							'de-DE',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_de_DE}/`
						)}`
					},
					{
						hrefLang: 'nl-nl',
						href: `${getBaseUrl('nl-NL')}${getLocalizedFromEnglish(
							'nl-NL',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_nl_NL}/`
						)}`
					},
					{
						hrefLang: 'es-es',
						href: `${getBaseUrl('es-ES')}${getLocalizedFromEnglish(
							'es-ES',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_es_ES}/`
						)}`
					},
					{
						hrefLang: 'es-mx',
						href: `${getBaseUrl('es-MX')}${getLocalizedFromEnglish(
							'es-MX',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_es_MX}/`
						)}`
					},
					{
						hrefLang: 'x-default',
						href: `${getBaseUrl()}${getLocalizedFromEnglish(
							'en-US',
							`/resources/${getResourcePageType(resource.type)}/${resource.slug_en_US}/`
						)}`
					}
				]}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
				openGraph={{
					title,
					description,
					url: `${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/resources/${getResourcePageType(resource.type)}/${slug}/`
					)}`,
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale),
					siteName: 'BigCommerce'
				}}
			/>
			<BreadcrumbJsonLd
				itemListElements={[
					{
						position: 1,
						name: 'Homepage',
						item: `${getBaseUrl(locale)}/`
					},
					{
						position: 2,
						name: 'Resources',
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(locale as Locale, `/resources`)}`
					},
					{
						position: 3,
						name: `BigCommerce ${capitalize(getResourcePageType(resource.type))}`,
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(
							locale as Locale,
							`/resources/${getResourcePageType(resource.type)}`
						)}`
					},
					{
						position: 4,
						name: resource.title,
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(
							locale as Locale,
							`/resources/${getResourcePageType(resource.type)}/${slug}`
						)}`
					}
				]}
			/>

			{children}
		</>
	)
}
